<template>
  <el-dialog
    custom-class="batch-progress-dialog"
    :visible="isOpen"
    :close-on-click-modal="false"
    :show-close="false"
    :destroy-on-close="true"
    width="960px"
    top="6vh"
    @update:visible="(v) => $emit('input', v)"
    @close="closeRequest"
  >
    <div v-if="batch" class="batch-progress">

      <!-- Topbar -->
      <div class="bp-topbar">
        <div class="bp-topbar-left">
          <button type="button" class="bp-close-btn" @click="closeRequest" aria-label="Close">
            <i class="el-icon-close"/>
          </button>
          <div class="bp-title-block">
            <div class="bp-title-row">
              <span class="qd-eyebrow">{{ statusEyebrow }}</span>
              <el-tooltip
                v-if="isProcessFailed"
                effect="dark"
                placement="top"
                :content="processFailedReason"
              >
                <span class="programs-status-pill" :class="statusToneClass">{{ statusLabel }}</span>
              </el-tooltip>
              <span v-else class="programs-status-pill" :class="statusToneClass">{{ statusLabel }}</span>
            </div>
            <div class="bp-batch-title">{{ batch.name || 'Untitled run' }}</div>
          </div>
        </div>
        <!-- "View program" lived here as a disabled stub; hidden until
             it's actually wired up so it doesn't read as a dead control. -->
      </div>

      <!-- Body -->
      <div class="bp-body">

        <!-- Header / summary card -->
        <section class="bp-card">
          <div class="bp-stats">
            <div class="bp-stat">
              <span class="qd-eyebrow">Enrolled</span>
              <div class="bp-stat-num">{{ enrolledCount }}</div>
              <span class="bp-stat-sub">{{ enrolledSub }}</span>
            </div>
            <div class="bp-stat">
              <span class="qd-eyebrow">In progress</span>
              <div class="bp-stat-num">{{ inProgressCount }}</div>
              <span class="bp-stat-sub">{{ inProgressSub }}</span>
            </div>
            <div class="bp-stat">
              <span class="qd-eyebrow">Completed</span>
              <div class="bp-stat-num">{{ completedCount }}</div>
              <span class="bp-stat-sub">{{ completedSub }}</span>
            </div>
            <div class="bp-stat">
              <span class="qd-eyebrow">Not started</span>
              <div class="bp-stat-num">{{ notStartedCount }}</div>
              <span class="bp-stat-sub">{{ notStartedSub }}</span>
            </div>
          </div>

          <div class="bp-progress">
            <div class="bp-progress-bar">
              <div
                class="bp-progress-fill"
                :style="{ width: progressPercent + '%' }"
              ></div>
            </div>
            <span class="bp-progress-pct">{{ progressPercent }}%</span>
          </div>
          <div class="bp-progress-sub">
            {{ progressSubLabel }}
          </div>
        </section>

        <!-- Meta card -->
        <section class="bp-card">
          <div class="bp-card-head">
            <span class="qd-eyebrow">Run details</span>
          </div>
          <div class="bp-meta-list">
            <div class="bp-meta-row">
              <span class="qd-eyebrow">Program</span>
              <span class="bp-meta-value">{{ batch.programTitle || '—' }}</span>
            </div>
            <div class="bp-meta-row">
              <span class="qd-eyebrow">Start date</span>
              <span class="bp-meta-value">{{ formatDate(batch.startDate) }}</span>
            </div>
            <div class="bp-meta-row">
              <span class="qd-eyebrow">Created</span>
              <span class="bp-meta-value">{{ formatDate(batch.createdAt) }}</span>
            </div>
            <div class="bp-meta-row">
              <span class="qd-eyebrow">Audience</span>
              <span class="bp-meta-value">{{ audienceSummary }}</span>
            </div>
          </div>
        </section>

        <!-- Program timeline — vertical timeline of items so the admin can
             see at a glance what's done, what's today, and what's still
             ahead. Drives clarity that the aggregate progress bar can't. -->
        <section v-if="timeline.length" class="bp-card bp-timeline-card">
          <div class="bp-card-head">
            <span class="qd-eyebrow">Program timeline</span>
            <span class="bp-card-helper">
              {{ timeline.length }} {{ timeline.length === 1 ? 'item' : 'items' }}
            </span>
          </div>

          <ol class="bp-timeline">
            <li
              v-for="row in timeline"
              :key="row.id"
              class="bp-timeline-row"
              :class="['is-' + row.state, { 'is-today': row.isToday, 'is-last': row.isLast }]"
            >
              <div class="bp-timeline-spine">
                <span class="bp-timeline-node">
                  <i v-if="row.state === 'complete'" class="el-icon-check"/>
                  <i v-else-if="row.state === 'today'" class="el-icon-time"/>
                  <i v-else-if="row.state === 'skipped'" class="el-icon-minus"/>
                </span>
                <span v-if="!row.isLast" class="bp-timeline-line"></span>
              </div>

              <div class="bp-timeline-body">
                <div class="bp-timeline-header">
                  <span class="bp-timeline-day">{{ row.dayLabel }}</span>
                  <span class="bp-timeline-sep">·</span>
                  <span class="bp-timeline-date">{{ row.dateLabel }}</span>
                  <span class="bp-timeline-type">{{ row.typeLabel }}</span>
                  <span class="bp-timeline-pill" :class="'is-' + row.state">
                    {{ row.statusLabel }}
                  </span>
                </div>

                <div v-if="row.title" class="bp-timeline-title">{{ row.title }}</div>

                <div class="bp-timeline-footer">
                  <div class="bp-timeline-bar">
                    <div
                      class="bp-timeline-bar-fill"
                      :style="{ width: row.barWidth }"
                    ></div>
                  </div>
                  <span class="bp-timeline-footer-label">{{ row.footerLabel }}</span>
                </div>
              </div>
            </li>
          </ol>
        </section>

        <!-- Participants card -->
        <section class="bp-card">
          <div class="bp-card-head">
            <span class="qd-eyebrow">Participants</span>
            <span v-if="participants.length" class="bp-card-helper">
              {{ participants.length }} {{ participants.length === 1 ? 'person' : 'people' }}
            </span>
          </div>

          <div v-if="loading" class="bp-card-loading">
            <i class="el-icon-loading"/>
            <span>Loading participant progress…</span>
          </div>

          <div v-else-if="!participants.length" class="programs-empty-card">
            <div class="programs-empty-bubble">
              <i class="el-icon-data-line"/>
            </div>
            <div class="programs-empty-title">No participant data yet</div>
            <div class="programs-empty-sub">
              Once the delivery service finishes processing this run, each
              enrolee's progress will appear here.
            </div>
          </div>

          <el-table
            v-else
            :data="participants"
            class="bp-participants-table"
            :default-sort="{ prop: 'completedCount', order: 'descending' }"
          >
            <el-table-column label="Participant" min-width="280">
              <template slot-scope="scope">
                <div class="bp-participant-name">
                  {{ scope.row.name || scope.row.email }}
                </div>
                <div class="bp-participant-meta">
                  <span class="bp-participant-team" :class="{ 'is-muted': !scope.row.teamName }">
                    {{ scope.row.teamName || 'No team' }}
                  </span>
                  <template v-if="scope.row.showSecondaryEmail">
                    <span class="bp-participant-meta-dot">·</span>
                    <span class="bp-participant-email">{{ scope.row.email }}</span>
                  </template>
                </div>
                <div class="bp-participant-sub">
                  Last activity: {{ formatDate(scope.row.lastActivity) }}
                </div>
              </template>
            </el-table-column>
            <el-table-column
              label="Progress"
              min-width="220"
              sortable
              :sort-method="sortByPercent"
            >
              <template slot-scope="scope">
                <div class="bp-participant-progress">
                  <div class="bp-participant-bar">
                    <div
                      class="bp-participant-fill"
                      :style="{ width: scope.row.percent + '%' }"
                    ></div>
                  </div>
                  <span class="bp-participant-pct">{{ scope.row.percent }}%</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column
              label="Completed"
              width="120"
              prop="completedCount"
              sortable
            >
              <template slot-scope="scope">
                <span class="bp-participant-num">
                  <strong>{{ scope.row.completedCount }}</strong>
                  <span class="bp-participant-num-total">/ {{ scope.row.totalCount }}</span>
                </span>
              </template>
            </el-table-column>
            <el-table-column label="Status" width="120">
              <template slot-scope="scope">
                <span class="programs-status-pill" :class="participantToneClass(scope.row)">
                  {{ participantStatusLabel(scope.row) }}
                </span>
              </template>
            </el-table-column>
          </el-table>
        </section>

      </div>

    </div>
  </el-dialog>
</template>

<script>
import firebase from 'firebase/app'
import 'firebase/firestore'
import { dayLabel, formatBatchFailure, formatScheduleDate, itemTypeLabel, todayIso, toIsoDate } from './_program-labels'

const TIMELINE_STATUS_LABEL = {
  skipped: 'Skipped',
  complete: 'Complete',
  today: 'Today',
  inflight: 'In progress',
  upcoming: 'Upcoming',
}

const SKIP_REASON_LABEL = {
  custom_question_source_not_found: 'Source question was missing when this batch ran',
  custom_question_missing_libraryId: 'Item was missing a question reference',
}

function deriveTimelineState({ isSkipped, isComplete, isToday, isPast }) {
  if (isSkipped) return 'skipped'
  if (isComplete) return 'complete'
  if (isToday) return 'today'
  if (isPast) return 'inflight'
  return 'upcoming'
}

export default {
  name: 'batch-progress',
  props: {
    value: { type: Boolean, default: false },
    batch: { type: Object, default: null },
  },
  data() {
    return {
      deliveries: [],
      // Custom-question items are dual-written to users_questions, not
      // users_deliveries — querying both is the only way the timeline can
      // show accurate completion for the question rows.
      programQuestions: [],
      loading: false,
    }
  },
  computed: {
    isOpen() {
      return Boolean(this.value && this.batch)
    },
    statusEyebrow() {
      const s = (this.batch && this.batch.status) || 'draft'
      // process_failed renders "PROCESS_FAILED" which wraps awkwardly in
      // the topbar — keep the eyebrow short and friendly.
      if (s === 'process_failed') return 'FAILED'
      return String(s).toUpperCase()
    },
    statusLabel() {
      const s = (this.batch && this.batch.status) || ''
      if (!s) return 'Draft'
      if (s === 'process_failed') return 'Failed'
      return s.charAt(0).toUpperCase() + s.slice(1)
    },
    statusToneClass() {
      switch ((this.batch && this.batch.status) || '') {
        case 'active':         return 'is-active'
        case 'scheduled':      return 'is-scheduled'
        case 'completed':      return 'is-completed'
        case 'process_failed': return 'is-failed'
        case 'archived':       return 'is-draft'
        default:               return 'is-draft'
      }
    },
    isProcessFailed() {
      return Boolean(this.batch && this.batch.status === 'process_failed')
    },
    processFailedReason() {
      // Tooltip on the failed status pill. Show the friendly explanation;
      // the technical detail is reserved for the Retry confirm dialog.
      const { friendly } = formatBatchFailure(this.batch && this.batch.processFailedReason)
      return friendly
    },
    enrolledCount() {
      if (!this.batch) return 0
      const enrolees = Array.isArray(this.batch.enrolees) ? this.batch.enrolees : []
      if (enrolees.length) return enrolees.length
      const sels = (this.batch.audienceSnapshot && this.batch.audienceSnapshot.selections) || []
      return sels.length
    },
    enrolledSub() {
      if (!this.batch) return '—'
      const enrolees = Array.isArray(this.batch.enrolees) ? this.batch.enrolees : []
      if (enrolees.length) return enrolees.length === 1 ? 'participant' : 'participants'
      return 'from audience snapshot'
    },
    // `recipientId` on a delivery is the user's email; resolve name + team
    // from the same Vuex source the enrolment picker uses, scoped to the
    // emails this batch actually has deliveries for so we don't walk the
    // entire org directory on every render.
    allUsers() {
      return (this.$store && this.$store.getters['question/allUsers']) || {}
    },
    teamAll() {
      return (this.$store && this.$store.getters['question/teamAll']) || {}
    },
    teamNameById() {
      const out = {}
      const src = this.teamAll || {}
      Object.keys(src).forEach((k) => {
        const t = src[k]
        if (!t || typeof t !== 'object') return
        const id = String(t.key || t.id || k || '')
        const name = String(t.name || t.label || '')
        if (id && name) out[id] = name
      })
      return out
    },
    deliveryEmails() {
      const set = new Set()
      this.deliveries.forEach((d) => {
        const e = String(d.recipientId || '').trim().toLowerCase()
        if (e) set.add(e)
      })
      return set
    },
    directoryByEmail() {
      const map = {}
      const users = this.allUsers || {}
      const teams = this.teamNameById
      const needed = this.deliveryEmails
      if (!needed.size) return map
      Object.keys(users).forEach((k) => {
        const u = users[k]
        if (!u || typeof u !== 'object') return
        const email = String(u.email || k || '').trim().toLowerCase()
        if (!email || !needed.has(email)) return
        const name = String(
          u.fullname ||
          u.full_name ||
          u.displayName ||
          u.name ||
          [u.firstname, u.lastname].filter(Boolean).join(' ').trim() ||
          ''
        ).trim()
        const rawTeam = u.team || u.team_id || u.teamId
        let teamIds = []
        if (Array.isArray(rawTeam)) teamIds = rawTeam.map((v) => String(v || '')).filter(Boolean)
        else if (rawTeam && typeof rawTeam === 'object') {
          teamIds = [rawTeam.key, rawTeam.id, rawTeam.value].map((v) => String(v || '')).filter(Boolean)
        } else if (rawTeam) teamIds = [String(rawTeam)]
        const teamName = teamIds.map((id) => teams[id]).filter(Boolean).join(', ')
        map[email] = { name, teamName }
      })
      return map
    },
    // Per-participant aggregation built from the deliveries[] this component
    // fetches when opened. participants[] also drives the bottom table.
    participants() {
      const map = {}
      this.deliveries.forEach((d) => {
        const k = d.recipientId
        if (!map[k]) {
          map[k] = {
            recipientId: k,
            totalCount: 0,
            completedCount: 0,
            openCount: 0,
            terminalCount: 0,
            lastActivity: null,
          }
        }
        const row = map[k]
        row.totalCount += 1
        if (d.status === 'completed') {
          row.completedCount += 1
          row.terminalCount += 1
          if (!row.lastActivity || (d.completedAt && d.completedAt > row.lastActivity)) {
            row.lastActivity = d.completedAt || d.updatedAt || null
          }
        } else if (d.status === 'expired' || d.status === 'skipped') {
          row.terminalCount += 1
        } else {
          row.openCount += 1
        }
      })
      return Object.values(map).map((row) => {
        const email = String(row.recipientId || '')
        const info = this.directoryByEmail[email.toLowerCase()] || {}
        const name = info.name || ''
        return {
          ...row,
          email,
          name,
          teamName: info.teamName || '',
          // showSecondaryEmail: when name is present we already used the
          // email as the fallback display, so suppress the second email line.
          showSecondaryEmail: Boolean(name),
          percent: row.totalCount ? Math.round((row.completedCount / row.totalCount) * 100) : 0,
        }
      })
    },
    inProgressCount() {
      // Anyone who has at least one open AND at least one completed delivery.
      return this.participants.filter((p) => p.openCount > 0 && p.completedCount > 0).length
    },
    completedCount() {
      // Anyone who has finished every delivery.
      return this.participants.filter((p) => p.totalCount > 0 && p.completedCount === p.totalCount).length
    },
    notStartedCount() {
      // Anyone with zero completions yet.
      return this.participants.filter((p) => p.completedCount === 0).length
    },
    inProgressSub() {
      return this.participants.length ? 'with at least one done' : 'No deliveries yet'
    },
    completedSub() {
      return this.participants.length ? 'all items done' : 'No deliveries yet'
    },
    notStartedSub() {
      return this.participants.length ? 'no completions yet' : 'Awaiting delivery'
    },
    progressPercent() {
      const total = this.deliveries.length
      const done = this.deliveries.filter((d) => d.status === 'completed').length
      if (total) return Math.round((done / total) * 100)
      // Fall back to the denormalized rollup on the batch doc when we have it.
      const p = (this.batch && this.batch.progress) || {}
      const pTotal = p.totalDeliveries || 0
      const pDone = p.completedDeliveries || 0
      if (!pTotal) return 0
      return Math.round((pDone / pTotal) * 100)
    },
    progressSubLabel() {
      const total = this.deliveries.length
      if (total) {
        const done = this.deliveries.filter((d) => d.status === 'completed').length
        return `${done} of ${total} deliveries completed`
      }
      const p = (this.batch && this.batch.progress) || {}
      if (p.totalDeliveries) {
        return `${p.completedDeliveries || 0} of ${p.totalDeliveries} deliveries completed`
      }
      return 'Progress will populate when deliveries start firing.'
    },
    audienceSummary() {
      const sels = (this.batch && this.batch.audienceSnapshot && this.batch.audienceSnapshot.selections) || []
      if (!sels.length) return '—'
      const counts = sels.reduce((acc, s) => {
        acc[s.kind] = (acc[s.kind] || 0) + 1
        return acc
      }, {})
      const parts = []
      if (counts.cohort) parts.push(`${counts.cohort} cohort${counts.cohort === 1 ? '' : 's'}`)
      if (counts.team)   parts.push(`${counts.team} team${counts.team === 1 ? '' : 's'}`)
      if (counts.person) parts.push(`${counts.person} ${counts.person === 1 ? 'person' : 'people'}`)
      return parts.join(' · ') || '—'
    },
    // The batch doc doesn't snapshot the items list — read the program
    // from the store the parent already populated.
    program() {
      const id = this.batch && this.batch.programId
      if (!id) return null
      const all = (this.$store && this.$store.getters['programs/programs']) || []
      return all.find((p) => p && p.id === id) || null
    },
    programItems() {
      const items = (this.program && this.program.items) || []
      return [...items]
        .filter((it) => it && it.type)
        .sort((a, b) => (Number(a.offsetDays) || 0) - (Number(b.offsetDays) || 0))
    },
    skippedItemIds() {
      const list = (this.batch && this.batch.skippedItems) || []
      const map = {}
      list.forEach((s) => {
        if (s && s.itemId) map[s.itemId] = s.reason || 'skipped'
      })
      return map
    },
    deliveriesByOffsetDay() {
      const map = {}
      this.deliveries.forEach((d) => {
        const k = String(d.programOffsetDay != null ? d.programOffsetDay : d.priority)
        if (!map[k]) map[k] = []
        map[k].push(d)
      })
      return map
    },
    questionsByQuestId() {
      // libraryId on a program custom_question item == users_questions.quest_id.
      const map = {}
      this.programQuestions.forEach((q) => {
        const k = String(q.quest_id || q.libraryId || '')
        if (!k) return
        if (!map[k]) map[k] = []
        map[k].push(q)
      })
      return map
    },
    timeline() {
      const items = this.programItems
      if (!items.length || !this.batch) return []
      const start = this.batch.startDate
      if (!start) return []
      const base = new Date(`${start}T00:00:00`)
      if (Number.isNaN(base.getTime())) return []

      const totalEnrolled = (Array.isArray(this.batch.enrolees) && this.batch.enrolees.length) ||
        ((this.batch.audienceSnapshot && this.batch.audienceSnapshot.selections) || []).length

      const skipMap = this.skippedItemIds
      const today = todayIso()
      const lastIdx = items.length - 1

      return items.map((it, idx) => {
        const offsetDays = Number(it.offsetDays) || 0
        const d = new Date(base)
        d.setDate(d.getDate() + offsetDays)
        const dateIso = toIsoDate(d)

        const { completedCount, totalCount } = this._completionFor(it, offsetDays, totalEnrolled)

        const isSkipped = Boolean(skipMap[it.id])
        const isPast = dateIso < today
        const isToday = dateIso === today
        // Sessions flip to delivery `status: "completed"` on signup
        // (FN's onClassroomsSignup), but the event itself can still be in
        // the future — don't promote the row to "Complete" until the
        // scheduled day has passed. Signups still fill the progress bar.
        const allDone = totalCount > 0 && completedCount >= totalCount
        const isComplete = !isSkipped && allDone && (it.type !== 'session' || isPast)
        const state = deriveTimelineState({ isSkipped, isComplete, isToday, isPast })
        const percent = totalCount ? Math.round((completedCount / totalCount) * 100) : 0
        const skipReason = skipMap[it.id] || ''

        return {
          id: it.id,
          offsetDays,
          dayLabel: dayLabel(offsetDays),
          dateIso,
          dateLabel: formatScheduleDate(d),
          typeLabel: itemTypeLabel(it.type),
          title: String(it.participantTitle || '').trim(),
          state,
          isToday,
          isSkipped,
          skipReason,
          completedCount,
          totalCount,
          percent,
          statusLabel: TIMELINE_STATUS_LABEL[state] || 'Upcoming',
          footerLabel: this._footerLabelFor({ state, isSkipped, skipReason, totalCount, completedCount }),
          barWidth: state === 'upcoming' || isSkipped ? '0%' : `${percent}%`,
          isLast: idx === lastIdx,
        }
      })
    },
  },
  watch: {
    isOpen(open) {
      if (open) this.fetchProgress()
      else {
        this.deliveries = []
        this.programQuestions = []
      }
    },
  },
  methods: {
    async fetchProgress() {
      if (!this.batch || !this.batch.id) return
      this.loading = true
      try {
        // Run both reads in parallel — independent collections.
        const db = firebase.firestore()
        const [delSnap, qSnap] = await Promise.all([
          db.collection('users_deliveries').where('batchId', '==', this.batch.id).get(),
          db.collection('users_questions').where('batchId', '==', this.batch.id).get(),
        ])
        this.deliveries = delSnap.docs.map((d) => ({ id: d.id, ...d.data() }))
        this.programQuestions = qSnap.docs.map((d) => ({ id: d.id, ...d.data() }))
      } catch (err) {
        // tslint:disable-next-line:no-console
        console.error('[batch-progress] fetchProgress failed:', err)
        this.deliveries = []
        this.programQuestions = []
      } finally {
        this.loading = false
      }
    },
    sortByPercent(a, b) {
      return a.percent - b.percent
    },
    participantToneClass(p) {
      if (p.totalCount === 0) return 'is-draft'
      if (p.completedCount === p.totalCount) return 'is-completed'
      if (p.completedCount > 0) return 'is-active'
      return 'is-scheduled'
    },
    participantStatusLabel(p) {
      if (p.totalCount === 0) return '—'
      if (p.completedCount === p.totalCount) return 'Done'
      if (p.completedCount > 0) return 'In progress'
      return 'Not started'
    },
    formatDate(iso) {
      if (!iso) return '—'
      try {
        return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
      } catch (e) {
        return '—'
      }
    },
    // Per-item completion. Custom questions live in users_questions (any
    // non-'open' status counts as answered); everything else lives in
    // users_deliveries (status === 'completed').
    _completionFor(item, offsetDays, totalEnrolled) {
      if (item.type === 'custom_question') {
        const rows = this.questionsByQuestId[String(item.libraryId || '')] || []
        return {
          totalCount: rows.length || totalEnrolled,
          completedCount: rows.filter((q) => q.status && q.status !== 'open').length,
        }
      }
      const rows = this.deliveriesByOffsetDay[String(offsetDays)] || []
      const sameType = rows.filter((r) => !r.itemType || r.itemType === item.type)
      const pool = sameType.length ? sameType : rows
      return {
        totalCount: pool.length || totalEnrolled,
        completedCount: pool.filter((r) => r.status === 'completed').length,
      }
    },
    _footerLabelFor({ state, isSkipped, skipReason, totalCount, completedCount }) {
      if (isSkipped) return SKIP_REASON_LABEL[skipReason] || 'Skipped during this run'
      if (state === 'upcoming') return 'Upcoming'
      if (!totalCount) return state === 'today' ? 'Sent today' : 'Waiting on activity'
      return `${completedCount} of ${totalCount} completed`
    },
    closeRequest() {
      this.$emit('input', false)
    },
  },
}
</script>

<style>
.batch-progress-dialog {
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(108, 48, 204, 0.06), 0 24px 48px rgba(15, 16, 32, 0.18);
  overflow: hidden;
  max-width: calc(100vw - 32px);
}
.batch-progress-dialog .el-dialog__header { display: none; }
.batch-progress-dialog .el-dialog__body { padding: 0; }
</style>

<style scoped>
.batch-progress {
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 12vh);
}

/* Topbar */
.bp-topbar {
  display: flex;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #eef0f5;
  flex-shrink: 0;
}
.bp-topbar-left {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}
.bp-close-btn {
  appearance: none;
  background: transparent;
  border: 1px solid #e2e4ea;
  color: #6b7280;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  flex-shrink: 0;
  transition: border-color .15s ease, color .15s ease, background .15s ease;
}
.bp-close-btn:hover {
  border-color: rgba(108, 48, 204, 0.30);
  color: #1a1a1a;
  background: #f2ecfb;
}
.bp-title-block { min-width: 0; }
.bp-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.bp-batch-title {
  font-size: 16px;
  font-weight: 800;
  color: #1a1a1a;
  letter-spacing: -0.005em;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 640px;
}
/* Body */
.bp-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* Cards */
.bp-card {
  background: #fff;
  border: 1px solid #eef0f5;
  border-radius: 12px;
  padding: 20px 22px;
}
.bp-card-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 14px;
}


/* Stats strip */
.bp-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 18px;
}
.bp-stat {
  background: #fafbfd;
  border: 1px solid #eef0f5;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.bp-stat-num {
  font-size: 26px;
  font-weight: 800;
  color: #1a1a1a;
  letter-spacing: -0.01em;
  line-height: 1.1;
  margin-top: 2px;
}
.bp-stat-sub {
  font-size: 11px;
  color: #8a8a8a;
  font-weight: 600;
}

/* Big progress bar */
.bp-progress {
  display: flex;
  align-items: center;
  gap: 12px;
}
.bp-progress-bar {
  flex: 1;
  height: 10px;
  background: #eef0f5;
  border-radius: 999px;
  overflow: hidden;
}
.bp-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #6c30cc 0%, #8a4ce0 100%);
  border-radius: 999px;
  transition: width .3s ease;
}
.bp-progress-pct {
  font-size: 13px;
  font-weight: 800;
  color: #1a1a1a;
  min-width: 44px;
  text-align: right;
}
.bp-progress-sub {
  margin-top: 8px;
  font-size: 12px;
  color: #6b7280;
}

/* Meta list */
.bp-meta-list {
  display: flex;
  flex-direction: column;
}
.bp-meta-row {
  display: grid;
  grid-template-columns: 140px 1fr;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #f3f4f6;
}
.bp-meta-row:last-child {
  border-bottom: none;
}
.bp-meta-value {
  font-size: 13px;
  color: #1a1a1a;
  font-weight: 600;
  word-break: break-word;
}

/* Status pill — redeclared per spec (don't inherit across components) */
.programs-status-pill {
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 999px;
}
.programs-status-pill.is-draft     { color: #8a8a8a; background: #f6f7f9; }
.programs-status-pill.is-scheduled { color: #c89500; background: rgba(247, 185, 36, 0.12); }
.programs-status-pill.is-active    { color: #00b383; background: rgba(0, 179, 131, 0.10); }
.programs-status-pill.is-completed { color: #6c30cc; background: #f2ecfb; }
.programs-status-pill.is-failed    { color: #cc3030; background: rgba(204, 48, 48, 0.10); }

/* Empty state — mirrors .programs-empty-card from programs.vue */
.programs-empty-card {
  text-align: center;
  padding: 48px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.programs-empty-bubble {
  width: 56px;
  height: 56px;
  border-radius: 999px;
  background: #f6f7f9;
  border: 1px solid #eef0f5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  color: #8a8a8a;
  margin-bottom: 16px;
}
.programs-empty-title {
  font-size: 16px;
  font-weight: 800;
  color: #1a1a1a;
  margin-bottom: 6px;
}
.programs-empty-sub {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.55;
  max-width: 480px;
}

/* ── Program timeline ──────────────────────────────────────────────
   Vertical timeline: spine on the left (node + connecting line), body
   on the right (header row · title · progress footer). Today's row is
   tinted brand purple; skipped rows fade into the background. */
.bp-timeline {
  list-style: none;
  padding: 0;
  margin: 0;
}
.bp-timeline-row {
  display: grid;
  grid-template-columns: 28px 1fr;
  gap: 14px;
  padding: 10px 12px 14px 8px;
  border-radius: 10px;
  position: relative;
  transition: background-color .15s ease;
}
.bp-timeline-row.is-today {
  background: #f6f0fc;
  box-shadow: inset 3px 0 0 #6c30cc;
}
.bp-timeline-row.is-skipped {
  opacity: 0.72;
}

/* Spine */
.bp-timeline-spine {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 4px;
}
.bp-timeline-node {
  width: 22px;
  height: 22px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 2px solid #d6dae3;
  color: #fff;
  font-size: 11px;
  flex-shrink: 0;
  z-index: 1;
  transition: background-color .15s ease, border-color .15s ease;
}
.bp-timeline-row.is-complete .bp-timeline-node {
  background: #00b383;
  border-color: #00b383;
}
.bp-timeline-row.is-today .bp-timeline-node {
  background: #6c30cc;
  border-color: #6c30cc;
  box-shadow: 0 0 0 4px rgba(108, 48, 204, 0.14);
}
.bp-timeline-row.is-inflight .bp-timeline-node {
  background: #fff;
  border-color: #6c30cc;
}
.bp-timeline-row.is-inflight .bp-timeline-node::after {
  content: '';
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #6c30cc;
}
.bp-timeline-row.is-skipped .bp-timeline-node {
  background: #f6f7f9;
  border-color: #d6dae3;
  color: #8a8a8a;
}
.bp-timeline-line {
  flex: 1;
  width: 2px;
  background: #e8eaf0;
  margin-top: 4px;
  min-height: 18px;
}
.bp-timeline-row.is-complete .bp-timeline-line { background: #d5f0e6; }
.bp-timeline-row.is-today .bp-timeline-line { background: linear-gradient(180deg, #6c30cc 0%, #e8eaf0 100%); }

/* Body */
.bp-timeline-body {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.bp-timeline-header {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
}
.bp-timeline-day {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6c30cc;
}
.bp-timeline-row.is-skipped .bp-timeline-day,
.bp-timeline-row.is-upcoming .bp-timeline-day { color: #8a8a8a; }

.bp-timeline-sep {
  color: #c0c4cc;
  font-size: 12px;
}
.bp-timeline-date {
  font-size: 13px;
  font-weight: 700;
  color: #1a1a1a;
}
.bp-timeline-row.is-upcoming .bp-timeline-date,
.bp-timeline-row.is-skipped .bp-timeline-date { color: #6b7280; font-weight: 600; }

.bp-timeline-type {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
  background: #f3f4f8;
  padding: 3px 8px;
  border-radius: 6px;
}

.bp-timeline-pill {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 3px 9px;
  border-radius: 999px;
  color: #8a8a8a;
  background: #f6f7f9;
  flex-shrink: 0;
}
.bp-timeline-pill.is-complete { color: #007857; background: rgba(0, 179, 131, 0.12); }
.bp-timeline-pill.is-today    { color: #6c30cc; background: #efe5fb; }
.bp-timeline-pill.is-inflight { color: #6c30cc; background: rgba(108, 48, 204, 0.10); }
.bp-timeline-pill.is-skipped  { color: #b06000; background: rgba(247, 185, 36, 0.16); }

.bp-timeline-title {
  font-size: 13px;
  color: #1a1a1a;
  line-height: 1.45;
  word-break: break-word;
}
.bp-timeline-row.is-upcoming .bp-timeline-title,
.bp-timeline-row.is-skipped .bp-timeline-title { color: #6b7280; }

.bp-timeline-footer {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 2px;
}
.bp-timeline-bar {
  flex: 1;
  height: 5px;
  background: #eef0f5;
  border-radius: 999px;
  overflow: hidden;
}
.bp-timeline-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #6c30cc 0%, #8a4ce0 100%);
  border-radius: 999px;
  transition: width .3s ease;
}
.bp-timeline-row.is-complete .bp-timeline-bar-fill {
  background: linear-gradient(90deg, #00b383 0%, #1ec98f 100%);
}
.bp-timeline-row.is-skipped .bp-timeline-bar { background: #f3f4f8; }
.bp-timeline-footer-label {
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.bp-timeline-row.is-complete .bp-timeline-footer-label { color: #00805a; }
.bp-timeline-row.is-today .bp-timeline-footer-label,
.bp-timeline-row.is-inflight .bp-timeline-footer-label { color: #6c30cc; font-weight: 700; }

/* Participants */
.bp-card-helper {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #8a8a8a;
}
.bp-card-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px 0;
  font-size: 13px;
  color: #6b7280;
}
.bp-card-loading i {
  color: #6c30cc;
  font-size: 16px;
}
.bp-participants-table {
  width: 100%;
  border: 1px solid #e2e4ea;
  border-radius: 10px;
  overflow: hidden;
}
.bp-participant-name {
  font-size: 13px;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.35;
}
.bp-participant-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
  font-size: 12px;
  color: #6b7280;
  min-width: 0;
}
.bp-participant-team {
  font-weight: 600;
  color: #1a1a1a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 140px;
}
.bp-participant-team.is-muted {
  font-weight: 500;
  color: #8a8a8a;
}
.bp-participant-meta-dot {
  color: #c0c4cc;
  flex-shrink: 0;
}
.bp-participant-email {
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
.bp-participant-sub {
  font-size: 11px;
  color: #8a8a8a;
  margin-top: 2px;
}
.bp-participant-progress {
  display: flex;
  align-items: center;
  gap: 8px;
}
.bp-participant-bar {
  flex: 1;
  height: 6px;
  background: #eef0f5;
  border-radius: 999px;
  overflow: hidden;
}
.bp-participant-fill {
  height: 100%;
  background: linear-gradient(90deg, #6c30cc 0%, #8a4ce0 100%);
  border-radius: 999px;
  transition: width .3s ease;
}
.bp-participant-pct {
  font-size: 11px;
  font-weight: 700;
  color: #1a1a1a;
  min-width: 36px;
  text-align: right;
}
.bp-participant-num strong {
  font-size: 13px;
  font-weight: 800;
  color: #1a1a1a;
}
.bp-participant-num-total {
  font-size: 11px;
  color: #8a8a8a;
  margin-left: 2px;
}
</style>
